import { useSession } from "./auth/useSession";
import { useMemo } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import "./design/tokens.css";
import "./design/base.css";
import { configureClient } from "./data/client";
import * as config from "./config/config";
import { CreatorProvider, useCreator } from "./creator/CreatorContext";
import { Header } from "./routes/Header";
import { ErrorState, LoadingState } from "./routes/states";
import Gallery from "./routes/__gallery";
import Account from "./routes/Account";
import AssetDetail from "./routes/AssetDetail";
import Landing from "./routes/Landing";
import Welcome from "./routes/Welcome";
import { RequireSession } from "./routes/RequireSession";
import { PreviewBar } from "./routes/PreviewBar";
import { PreviewProvider } from "./preview/PreviewContext";
import Library from "./routes/Library";
import Upgrade from "./routes/Upgrade";
import StudioShell, { RequireOwner } from "./routes/studio/StudioShell";
import { StudioProvider } from "./routes/studio/StudioContext";
import StudioAssets from "./routes/studio/Assets";
import StudioAssetForm from "./routes/studio/AssetForm";
import StudioTiers from "./routes/studio/Tiers";
import StudioLists from "./routes/studio/Lists";
import PrivacyPolicyPage from "./routes/legal/PrivacyPolicyPage";
import TermsOfServicePage from "./routes/legal/TermsOfServicePage";

/**
 * `/` — the door for a visitor, the creator fork for the owner.
 *
 * Reads `me` from the creator context rather than Auth0: owning the campaign is
 * a fact the server states (`isOwner` from `/me`), not something the client can
 * work out from a token.
 */
function Root() {
  const { me } = useCreator();
  return me?.isOwner ? <Welcome /> : <Landing />;
}

/**
 * Every screen except the landing carries the app header.
 *
 * The preview bar sits *above* it and nothing else changes: seeing exactly what
 * a tier sees is the whole point, so a preview that altered the page would
 * answer a different question than the one asked.
 */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <>
      <PreviewBar />
      <Header />
      <main>{children}</main>
    </>
  );
}

export default function App() {
  const { isAuthenticated, isLoading } = useSession();

  /*
   * The composition root. Task 22 pointed the client at the API; task 9
   * removed the only thing it needed from React.
   *
   * There is no `getToken` any more. The session is an `httpOnly` cookie the
   * browser attaches by itself, so the client needs a base URL and nothing
   * else - which is why this is `useMemo` over an empty dependency list rather
   * than something that re-runs when identity changes. Identity is no longer
   * an input to it.
   */
  useMemo(() => {
    configureClient({ baseUrl: config.apiEndpoint });
  }, []);

  /*
   * Nothing renders until `GET /auth/session` has answered.
   *
   * While `isLoading` is true, `isAuthenticated` is false - not "unknown" -
   * and a signed-in visitor is indistinguishable from a signed-out one. Every
   * screen mounted in that window makes the wrong decision and does not
   * re-make it: the library fetched `/assets` with no token and cached the
   * 401 as its error state, and `RequireOwner` saw `me === null` and bounced
   * the owner to `/tabs`. Both were reported as separate bugs; they are this
   * one. Observed in sbx on 2026-08-30.
   *
   * This must stay after every hook above - it is an early return, and React
   * requires the hook order to be identical on every render.
   */
  if (isLoading) {
    return <LoadingState subLabel="RESTORING YOUR SESSION" />;
  }

  return (
    <BrowserRouter>
      <CreatorProvider
        signedIn={isAuthenticated}
        fallback={<LoadingState subLabel="LOADING THE LIBRARY" />}
        onError={(error) => (
          <ErrorState
            headline="THE LIBRARY DID NOT LOAD"
            detail="Nothing came back from the server. Reloading usually settles it."
            code={`CREATOR_LOAD_FAILED · ${error.name.toUpperCase()}`}
            onRetry={() => window.location.reload()}
          />
        )}
      >
        {/*
          Above the routes, so `?as=` survives navigation between patron screens
          and the bar persists rather than being remembered per screen.
        */}
        <PreviewProvider>
        <Routes>
          {/*
            `/` resolves from identity: the door for a visitor, the creator fork
            for the owner (`specs/perks-session-routing/` FR-4). It is what the
            base URL *renders*, never a redirect target, so a deep link is
            always honoured.
          */}
          <Route path="/" element={<Root />} />

          {/*
            Everything below needs a token. Guarded in front of the request,
            because reaching the screen and failing there produced
            UNAUTHENTICATEDERROR - a diagnosis the reader cannot act on, with
            the sign-in button on a page they were not sent to.
          */}
          <Route path="/tabs" element={<RequireSession><Shell><Library /></Shell></RequireSession>} />
          <Route path="/tabs/:slug" element={<RequireSession><Shell><AssetDetail /></Shell></RequireSession>} />
          {/* §23: a full screen, not a modal - so it carries no shell. */}
          {/* No Shell by design (§23), but the bar still belongs: the upgrade gate
    is precisely what previewing a low tier is meant to reveal. */}
          <Route
            path="/upgrade"
            element={<RequireSession><><PreviewBar /><Upgrade /></></RequireSession>}
          />
          <Route path="/account" element={<RequireSession><Shell><Account /></Shell></RequireSession>} />

          {/*
            Studio. The nesting is deliberate: RequireOwner turns a
            non-owner away before any /studio request is made, StudioProvider
            then loads the full creator record including the matrix, and
            StudioShell carries the chrome and the desktop-only notice. None of
            it is a boundary - the server checks OWNER_SUB on every /studio
            request (D-26).
          */}
          <Route
            path="/studio"
            element={
              <RequireSession>
              <RequireOwner>
                <StudioProvider>
                  <StudioShell />
                </StudioProvider>
              </RequireOwner>
              </RequireSession>
            }
          >
            <Route index element={<StudioAssets />} />
            <Route path="assets/:id" element={<StudioAssetForm />} />
            <Route path="tiers" element={<StudioTiers />} />
            <Route path="lists" element={<StudioLists />} />
          </Route>
          <Route path="/privacy-policy" element={<Shell><PrivacyPolicyPage /></Shell>} />
          <Route path="/terms-of-service" element={<Shell><TermsOfServicePage /></Shell>} />
          {/* Dev-only component gallery (task 5). Outside the shell on
              purpose - it is not a product screen. */}
          <Route path="/__gallery" element={<Gallery />} />
          {/* "/" decides by identity, so an unknown path needs no guess. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </PreviewProvider>
      </CreatorProvider>
    </BrowserRouter>
  );
}
